// shop-frontend/src/pages/ShopEditProduct.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { request, toAbsUrl } from "../utils/api";
import { uploadVariantImage } from "../utils/upload";

/* =========================
   Helpers
   ========================= */
function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function str(v) {
  return String(v || "").trim();
}
function normLower(v) {
  return str(v).toLowerCase();
}

function pickId(p) {
  return String(p?._id || p?.id || "").trim();
}

const SIZE_RE = /^(xs|s|m|l|xl|xxl|2xl|3xl|4xl|small|medium|large|x-large|\d+|\d+\.\d+)$/i;
const OPTION_TYPE_OPTIONS = ["Color", "Size", "Material", "Style"];

function splitOptionValues(input) {
  return String(input || "")
    .split(/[\n,،;]+/)
    .map((v) => String(v || "").trim())
    .filter(Boolean);
}

function cartesianOptionRows(opts) {
  const clean = (Array.isArray(opts) ? opts : [])
    .map((o) => ({ name: String(o?.name || "").trim(), values: Array.isArray(o?.values) ? o.values.map((v) => String(v || "").trim()).filter(Boolean) : [] }))
    .filter((o) => o.name && o.values.length > 0);
  if (!clean.length) return [];
  let rows = [[]];
  for (const opt of clean) {
    const next = [];
    for (const row of rows) for (const v of opt.values) next.push([...row, { name: opt.name, value: v }]);
    rows = next;
  }
  return rows;
}

export default function ShopEditProduct() {
  const nav = useNavigate();
  const params = useParams();
  const loc = useLocation();

  const id = useMemo(() => String(params.id || "").trim(), [params.id]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // editable fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [inStock, setInStock] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [category, setCategory] = useState("");
  const [shippingIsDigital, setShippingIsDigital] = useState(false);
  const [shippingWeightOz, setShippingWeightOz] = useState("");
  const [shippingLengthIn, setShippingLengthIn] = useState("");
  const [shippingWidthIn, setShippingWidthIn] = useState("");
  const [shippingHeightIn, setShippingHeightIn] = useState("");
  const [shippingPackageCode, setShippingPackageCode] = useState("package");
  const [weightUnit, setWeightUnit] = useState("oz"); // oz | g | kg | lb
  const [dimUnit, setDimUnit] = useState("in");       // in | cm

  function toOz(val, unit) {
    const n = Number(val); if (!Number.isFinite(n)) return n;
    if (unit === "g")  return n / 28.3495;
    if (unit === "kg") return n * 35.274;
    if (unit === "lb") return n * 16;
    return n;
  }
  function toIn(val, unit) {
    const n = Number(val); if (!Number.isFinite(n)) return n;
    if (unit === "cm") return n / 2.54;
    return n;
  }
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [uploadingColorKey, setUploadingColorKey] = useState("");
  const [colorStockDraft, setColorStockDraft] = useState({});

  // Option / variant builder (lets shop add variants to products that had none)
  const [optionTypeInput, setOptionTypeInput] = useState("");
  const [optionValuesInput, setOptionValuesInput] = useState("");

  // AI Targeting fields for Mall (matches backend TargetingSchema)
  const [targetGenders, setTargetGenders] = useState([]);
  const [targetMinAge, setTargetMinAge] = useState("");
  const [targetMaxAge, setTargetMaxAge] = useState("");
  const [targetInterests, setTargetInterests] = useState([]);
  const [targetSports, setTargetSports] = useState([]);

  const backTo = loc.state?.backTo || "/shop/products";

  /* =========================
     Load product
     ========================= */
  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    setOk("");

    try {
      if (!id) throw new Error("Missing product id");

      // ✅ FIX: remove 404 endpoint (/api/shop-products/:id)
      // ✅ Use the same base you use in utils/api.js for products
      const endpoints = [
        `/api/shop/products/${encodeURIComponent(id)}`,
        `/api/shops/products/${encodeURIComponent(id)}`,
        `/api/products/${encodeURIComponent(id)}`,
        `/api/public/products/${encodeURIComponent(id)}`,
      ];

      let res = null;
      let lastErr = null;

      for (const ep of endpoints) {
        try {
          res = await request(ep, { method: "GET" });
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      const p = res?.product || res?.data?.product || res?.data || res;

      if (!p || !pickId(p)) {
        throw new Error(lastErr?.message || "Product not found");
      }

      // fill form (support different backend keys)
      setTitle(str(p.title || p.name));
      setDescription(str(p.description));

      setCurrency(str(p.currency || "USD").toUpperCase());

      const pPrice = p.localPrice ?? p.price ?? p.amount ?? 0;
      setPrice(String(num(pPrice)));

      const pQty = p.quantity ?? p.stock ?? p.qty ?? 0;
      setQuantity(String(num(pQty)));

      // default true if missing
      setInStock(typeof p.inStock === "boolean" ? p.inStock : true);
      setIsActive(typeof p.isActive === "boolean" ? p.isActive : true);

      setCategory(str(p.categoryLabel || p.categoryName || p.category || ""));

      const sp = p.shippingProfile || {};
      setShippingIsDigital(Boolean(sp.isDigital));
      setShippingWeightOz(
        sp.weightOz != null
          ? String(sp.weightOz)
          : sp?.weight?.value != null
          ? String(sp.weight.value)
          : ""
      );
      setShippingLengthIn(
        sp.lengthIn != null
          ? String(sp.lengthIn)
          : sp?.dimensions?.length != null
          ? String(sp.dimensions.length)
          : ""
      );
      setShippingWidthIn(
        sp.widthIn != null
          ? String(sp.widthIn)
          : sp?.dimensions?.width != null
          ? String(sp.dimensions.width)
          : ""
      );
      setShippingHeightIn(
        sp.heightIn != null
          ? String(sp.heightIn)
          : sp?.dimensions?.height != null
          ? String(sp.dimensions.height)
          : ""
      );
      setShippingPackageCode(str(sp.packageCode || "package") || "package");

      const loadedOptions = Array.isArray(p.options)
        ? p.options
            .map((opt) => ({
              name: str(opt?.name),
              values: Array.isArray(opt?.values)
                ? opt.values.map((v) => str(v)).filter(Boolean)
                : [],
            }))
            .filter((opt) => opt.name && opt.values.length > 0)
        : [];

      const loadedVariants = Array.isArray(p.variants)
        ? p.variants.map((v) => ({
            optionValues: Array.isArray(v?.optionValues)
              ? v.optionValues
                  .map((ov) => ({ name: str(ov?.name), value: str(ov?.value) }))
                  .filter((ov) => ov.name && ov.value)
              : [],
            sku: str(v?.sku),
            stock: Math.max(0, num(v?.stock, 0)),
            priceOverride:
              v?.price === null || typeof v?.price === "undefined" ? "" : String(v.price),
            images: Array.isArray(v?.images) ? v.images.map((x) => str(x)).filter(Boolean) : [],
          }))
        : [];

      setOptions(loadedOptions);
      setVariants(loadedVariants);

      // Main product images
      const mainImgs = [
        ...(Array.isArray(p.images) ? p.images : []),
        ...(Array.isArray(p.displayImages) ? p.displayImages : []),
        ...(p.image ? [p.image] : []),
        ...(p.imageUrl ? [p.imageUrl] : []),
      ];
      setProductImages([...new Set(mainImgs.map(str).filter(Boolean))]);

      // Load AI targeting data (matches backend TargetingSchema)
      const t = p.targeting || {};
      setTargetGenders(Array.isArray(t.genders) ? t.genders : []);
      setTargetMinAge(t.minAge != null ? String(t.minAge) : "");
      setTargetMaxAge(t.maxAge != null ? String(t.maxAge) : "");
      setTargetInterests(Array.isArray(t.interests) ? t.interests : []);
      setTargetSports(Array.isArray(t.sports) ? t.sports : []);
    } catch (e) {
      setErr(e?.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  }, [id]);

  /* =========================
     Initial load (per id)
     ========================= */
  useEffect(() => {
    if (!id) {
      setErr("Missing product id");
      setLoading(false);
      return;
    }
    load();
  }, [id, load]);

  /* =========================
     Save
     ========================= */
  const save = useCallback(async () => {
    setSaving(true);
    setErr("");
    setOk("");

    try {
      if (!id) throw new Error("Missing product id");

      const cleanTitle = str(title);
      if (!cleanTitle) throw new Error("Title is required");

      const weightOzNum = toOz(shippingWeightOz, weightUnit);
      const lengthInNum = toIn(shippingLengthIn, dimUnit);
      const widthInNum  = toIn(shippingWidthIn, dimUnit);
      const heightInNum = toIn(shippingHeightIn, dimUnit);

      if (!shippingIsDigital) {
        if (
          !Number.isFinite(weightOzNum) ||
          weightOzNum < 0.1 ||
          !Number.isFinite(lengthInNum) ||
          lengthInNum < 1 ||
          !Number.isFinite(widthInNum) ||
          widthInNum < 1 ||
          !Number.isFinite(heightInNum) ||
          heightInNum < 1
        ) {
          throw new Error("Shipping profile is required: weightOz + lengthIn/widthIn/heightIn");
        }
      }

      const payload = {
        title: cleanTitle,
        description: str(description),
        localPrice: num(price, 0),
        quantity: num(quantity, 0),
        inStock: Boolean(inStock),
        isActive: Boolean(isActive),
        categoryLabel: str(category),
        shippingProfile: {
          isDigital: Boolean(shippingIsDigital),
          weightOz: shippingIsDigital ? null : weightOzNum,
          lengthIn: shippingIsDigital ? null : lengthInNum,
          widthIn: shippingIsDigital ? null : widthInNum,
          heightIn: shippingIsDigital ? null : heightInNum,
          packageCode: str(shippingPackageCode || "package") || "package",
        },
        images: productImages,
        options: options,
        variants: variants.map((v) => ({
          optionValues: Array.isArray(v.optionValues) ? v.optionValues : [],
          sku: str(v.sku),
          stock: Math.max(0, num(v.stock, 0)),
          price:
            v.priceOverride === "" || v.priceOverride === null || typeof v.priceOverride === "undefined"
              ? null
              : num(v.priceOverride, 0),
          images: Array.isArray(v.images) ? v.images.map((x) => str(x)).filter(Boolean) : [],
        })),

        // AI Targeting for Mall (matches backend TargetingSchema)
        targeting: {
          genders: Array.isArray(targetGenders) ? targetGenders : [],
          minAge: targetMinAge ? num(targetMinAge, 0) : 0,
          maxAge: targetMaxAge ? num(targetMaxAge, 100) : 100,
          interests: Array.isArray(targetInterests) ? targetInterests : [],
          sports: Array.isArray(targetSports) ? targetSports : [],
        },
      };

      // ✅ FIX: remove 404 endpoint (/api/shop-products/:id)
      const endpoints = [
        `/api/shop/products/${encodeURIComponent(id)}`,
        `/api/shops/products/${encodeURIComponent(id)}`,
        `/api/products/${encodeURIComponent(id)}`,
      ];

      let success = false;
      let lastErr = null;

      for (const ep of endpoints) {
        try {
          await request(ep, { method: "PUT", body: payload });
          success = true;
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (!success) {
        throw new Error(lastErr?.message || "Failed to save product");
      }

      setOk("Saved ✅");
    } catch (e) {
      setErr(e?.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  }, [id, title, description, price, quantity, inStock, isActive, category, shippingIsDigital, shippingWeightOz, shippingLengthIn, shippingWidthIn, shippingHeightIn, shippingPackageCode, weightUnit, dimUnit, options, variants, productImages, targetGenders, targetMinAge, targetMaxAge, targetInterests, targetSports]);

  const optionValueOf = useCallback((variant, optionName) => {
    const target = normLower(optionName);
    const found = (Array.isArray(variant?.optionValues) ? variant.optionValues : []).find(
      (ov) => normLower(ov?.name) === target
    );
    return str(found?.value);
  }, []);

  // Detect options by value content, not just name
  const looksLikeSizes = useCallback(
    (vals) => Array.isArray(vals) && vals.length > 0 && vals.every((v) => SIZE_RE.test(String(v).trim())),
    []
  );

  const colorOption = useMemo(() => {
    // 1. Prefer option explicitly named "color"
    const byName = options.find((o) => normLower(o?.name) === "color");
    if (byName) return byName;
    // 2. Find any option whose values DON'T look like sizes (could be named anything)
    return options.find((o) => Array.isArray(o.values) && o.values.length > 0 && !looksLikeSizes(o.values)) || null;
  }, [options, looksLikeSizes]);

  const sizeOption = useMemo(() => {
    // 1. Prefer option explicitly named "size"
    const byName = options.find((o) => normLower(o?.name) === "size");
    if (byName) return byName;
    // 2. Find option whose values all look like sizes (even if named something else)
    return options.find((o) => o !== colorOption && looksLikeSizes(o.values)) || null;
  }, [options, colorOption, looksLikeSizes]);

  const colorValues = useMemo(() => {
    if (colorOption?.values?.length) return colorOption.values;
    const fromVariants = variants.map((v) => optionValueOf(v, "color")).filter(Boolean);
    return [...new Set(fromVariants)];
  }, [colorOption, variants, optionValueOf]);

  const sizeValues = useMemo(() => {
    if (sizeOption?.values?.length) return sizeOption.values;
    const fromVariants = variants.map((v) => optionValueOf(v, "size")).filter(Boolean);
    return [...new Set(fromVariants)];
  }, [sizeOption, variants, optionValueOf]);

  const colorGroups = useMemo(() => {
    if (!colorOption) return [];
    return colorValues.map((color) => {
      const rows = (sizeOption ? sizeValues : [""]).map((size) => {
        const variant = variants.find((v) => {
          const c = normLower(optionValueOf(v, colorOption.name));
          const s = sizeOption ? normLower(optionValueOf(v, sizeOption.name)) : "";
          if (c !== normLower(color)) return false;
          return sizeOption ? s === normLower(size) : true;
        });
        return { size, variant };
      });

      const images = [
        ...new Set(
          rows.flatMap((r) => (Array.isArray(r.variant?.images) ? r.variant.images : [])).filter(Boolean)
        ),
      ];

      return { color, rows, images };
    });
  }, [colorOption, sizeOption, colorValues, sizeValues, variants, optionValueOf]);

  function updateVariantByColorAndSize(colorValue, sizeValue, patch) {
    setVariants((prev) =>
      prev.map((v) => {
        const c = normLower(optionValueOf(v, colorOption?.name || "color"));
        const s = sizeOption ? normLower(optionValueOf(v, sizeOption.name)) : "";
        const colorMatch = c === normLower(colorValue);
        const sizeMatch = sizeOption ? s === normLower(sizeValue) : true;
        return colorMatch && sizeMatch ? { ...v, ...patch } : v;
      })
    );
  }

  async function handleUploadMainImage(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (productImages.length >= 6) {
      setErr("Max 6 product photos allowed.");
      if (e?.target) e.target.value = "";
      return;
    }
    setUploadingMainImage(true);
    setErr("");
    try {
      const url = await uploadVariantImage(file);
      setProductImages((prev) => [...new Set([...prev, url])].slice(0, 6));
    } catch (uploadErr) {
      setErr(uploadErr?.message || "Image upload failed.");
    } finally {
      setUploadingMainImage(false);
      if (e?.target) e.target.value = "";
    }
  }

  function removeMainImage(url) {
    setProductImages((prev) => prev.filter((u) => u !== url));
  }

  async function handleUploadForColor(colorValue, e) {
    const file = e?.target?.files?.[0];
    if (!file || !colorOption) return;

    const colorKey = `${normLower(colorOption.name)}:${normLower(colorValue)}`;
    const currentImages = [
      ...new Set(
        variants
          .filter((v) => normLower(optionValueOf(v, colorOption.name)) === normLower(colorValue))
          .flatMap((v) => (Array.isArray(v.images) ? v.images : []))
          .filter(Boolean)
      ),
    ];

    if (currentImages.length >= 6) {
      setErr("You can upload up to 6 images per variant box.");
      if (e?.target) e.target.value = "";
      return;
    }

    setUploadingColorKey(colorKey);
    setErr("");
    try {
      const url = await uploadVariantImage(file);
      setVariants((prev) =>
        prev.map((v) => {
          if (normLower(optionValueOf(v, colorOption.name)) !== normLower(colorValue)) return v;
          const existing = Array.isArray(v.images) ? v.images : [];
          const next = existing.includes(url) ? existing : [...existing, url].slice(0, 6);
          return { ...v, images: next };
        })
      );
    } catch (uploadErr) {
      setErr(uploadErr?.message || "Variant image upload failed.");
    } finally {
      setUploadingColorKey("");
      if (e?.target) e.target.value = "";
    }
  }

  function variantKey(optionValues) {
    return (Array.isArray(optionValues) ? optionValues : [])
      .map((ov) => `${normLower(ov?.name)}:${normLower(ov?.value)}`)
      .sort()
      .join("|");
  }

  function addOption() {
    const name = str(optionTypeInput);
    if (!name) { setErr("Choose an option type."); return; }
    const values = splitOptionValues(optionValuesInput);
    if (values.length === 0) { setErr("Enter option values (comma-separated)."); return; }
    setErr("");
    setOptions((prev) => {
      const existingIdx = prev.findIndex((o) => normLower(o.name) === normLower(name));
      if (existingIdx !== -1) {
        // Merge new values into existing option (upsert)
        const existing = prev[existingIdx];
        const merged = [...new Set([...(existing.values || []), ...values])];
        return prev.map((o, i) => i === existingIdx ? { ...o, values: merged } : o);
      }
      return [...prev, { name, values }];
    });
    setOptionTypeInput("");
    setOptionValuesInput("");
  }

  function removeOption(name) {
    setOptions((prev) => prev.filter((o) => normLower(o.name) !== normLower(name)));
    setVariants([]);
  }

  function generateVariants() {
    if (!options.length) { setErr("Add at least one option first."); return; }
    const rows = cartesianOptionRows(options);
    if (rows.length === 0) { setErr("Add option values first."); return; }
    const byKey = new Map(variants.map((v) => [variantKey(v.optionValues), v]));
    const generated = rows.map((row) => {
      const key = variantKey(row);
      const existing = byKey.get(key);
      return {
        optionValues: row,
        sku: existing?.sku || "",
        priceOverride: existing?.priceOverride ?? "",
        stock: existing?.stock ?? 0,
        images: Array.isArray(existing?.images) ? existing.images : [],
      };
    });
    setVariants(generated);
    setErr("");
  }

  function removeColorImage(colorValue, url) {
    if (!colorOption) return;
    setVariants((prev) =>
      prev.map((v) => {
        if (normLower(optionValueOf(v, colorOption.name)) !== normLower(colorValue)) return v;
        return { ...v, images: (Array.isArray(v.images) ? v.images : []).filter((u) => u !== url) };
      })
    );
  }

  /* =========================
     Render
     ========================= */
  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.card}>Loading product…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div style={S.page}>
        <button style={S.btnGhost} onClick={() => nav(backTo)}>
          ← Back
        </button>

        <div style={{ height: 10 }} />

        <div style={S.card}>
          <div style={{ color: "#ff8080", fontWeight: 900 }}>Error</div>
          <div style={{ marginTop: 8 }}>{err}</div>

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button style={S.btnGhost} onClick={load}>
              Try again
            </button>
            <button style={S.btnGhost} onClick={() => nav(backTo)}>
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.btnGhost} onClick={() => nav(backTo)}>
          ← Back
        </button>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={S.btnGhost}
            onClick={() => nav(`/shop/product/${encodeURIComponent(id)}`)}
          >
            View Product
          </button>
          <button style={S.btnPrimary} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.h1}>Edit Product</div>
        <div style={S.sub}>Product ID: {id}</div>

        {!!ok && <div style={S.okBox}>{ok}</div>}
        {!!err && <div style={S.errBox}>{err}</div>}

        <div style={S.grid}>
          <div>
            <div style={S.label}>Title</div>
            <input
              style={S.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>Category (read-only — edit via Add Product)</div>
            <input style={{ ...S.input, opacity: 0.6, cursor: "not-allowed" }} value={category} disabled />
          </div>

          <div>
            <div style={S.label}>Currency (set by shop country)</div>
            <input style={{ ...S.input, opacity: 0.6, cursor: "not-allowed" }} value={currency} disabled />
          </div>

          <div>
            <div style={S.label}>Price</div>
            <input
              style={S.input}
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>Quantity</div>
            <input
              style={S.input}
              type="number"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>Stock Status</div>
            <select
              style={S.select}
              value={inStock ? "true" : "false"}
              onChange={(e) => setInStock(e.target.value === "true")}
            >
              <option value="true">In stock</option>
              <option value="false">Out of stock</option>
            </select>
          </div>

          <div>
            <div style={S.label}>Listing Status</div>
            <select
              style={S.select}
              value={isActive ? "true" : "false"}
              onChange={(e) => setIsActive(e.target.value === "true")}
            >
              <option value="true">Published (visible in mall)</option>
              <option value="false">Unpublished (hidden)</option>
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1", marginTop: 6 }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 8 }}>🚚 Shipping Profile</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
              Accurate shipping prices require real weight and dimensions.
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
              Missing shipping info will use defaults and may show incorrect rates.
            </div>
          </div>

          <div>
            <div style={S.label}>Digital product</div>
            <select
              style={S.select}
              value={shippingIsDigital ? "true" : "false"}
              onChange={(e) => setShippingIsDigital(e.target.value === "true")}
            >
              <option value="false">No (physical)</option>
              <option value="true">Yes (no shipping)</option>
            </select>
          </div>

          <div>
            <div style={S.label}>Package code</div>
            <select
              style={S.select}
              value={shippingPackageCode}
              onChange={(e) => setShippingPackageCode(e.target.value)}
            >
              <option value="package">package</option>
              <option value="small_flat_rate_box">small_flat_rate_box</option>
              <option value="medium_flat_rate_box">medium_flat_rate_box</option>
              <option value="large_flat_rate_box">large_flat_rate_box</option>
            </select>
          </div>

          <div>
            <div style={S.label}>Weight {shippingIsDigital ? "" : "*"}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                type="number"
                step="0.1"
                min="0"
                value={shippingWeightOz}
                onChange={(e) => setShippingWeightOz(e.target.value)}
                disabled={shippingIsDigital}
              />
              <select
                style={{ ...S.select, width: 70 }}
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
                disabled={shippingIsDigital}
              >
                <option value="oz">oz</option>
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="lb">lb</option>
              </select>
            </div>
          </div>

          <div>
            <div style={S.label}>Length {shippingIsDigital ? "" : "*"}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                type="number"
                step="0.1"
                min="0"
                value={shippingLengthIn}
                onChange={(e) => setShippingLengthIn(e.target.value)}
                disabled={shippingIsDigital}
              />
              <select
                style={{ ...S.select, width: 70 }}
                value={dimUnit}
                onChange={(e) => setDimUnit(e.target.value)}
                disabled={shippingIsDigital}
              >
                <option value="in">in</option>
                <option value="cm">cm</option>
              </select>
            </div>
          </div>

          <div>
            <div style={S.label}>Width {shippingIsDigital ? "" : "*"}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                type="number"
                step="0.1"
                min="0"
                value={shippingWidthIn}
                onChange={(e) => setShippingWidthIn(e.target.value)}
                disabled={shippingIsDigital}
              />
              <span style={{ lineHeight: "38px", fontSize: 12, color: "var(--text-secondary)", flexShrink: 0 }}>{dimUnit}</span>
            </div>
          </div>

          <div>
            <div style={S.label}>Height {shippingIsDigital ? "" : "*"}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                type="number"
                step="0.1"
                min="0"
                value={shippingHeightIn}
                onChange={(e) => setShippingHeightIn(e.target.value)}
                disabled={shippingIsDigital}
              />
              <span style={{ lineHeight: "38px", fontSize: 12, color: "var(--text-secondary)", flexShrink: 0 }}>{dimUnit}</span>
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <div style={S.label}>Description</div>
            <textarea
              style={S.textarea}
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* ===== Product Photos ===== */}
          <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 8 }}>🖼️ Product Photos</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>
              These are the main photos shown in the mall and product detail page. Up to 6 photos.
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              {productImages.map((url) => (
                <div key={url} style={S.variantImageCard}>
                  <img src={toAbsUrl(url)} alt="" style={S.variantImageThumb} />
                  <button
                    type="button"
                    style={S.variantImageRemove}
                    onClick={() => removeMainImage(url)}
                    disabled={saving}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {productImages.length < 6 && (
                <label style={S.photoUploadBox}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleUploadMainImage}
                    disabled={saving || uploadingMainImage}
                  />
                  {uploadingMainImage ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
                      Uploading…
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginTop: 4 }}>
                        Add Photo
                      </span>
                    </>
                  )}
                </label>
              )}
            </div>

            {productImages.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>
                No photos yet — upload at least one.
              </div>
            )}
          </div>

          {/* ===== Variants & Options (always visible) ===== */}
          <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>🧩 Variants & Options</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>
              Add options (Color, Size, etc.) then click <b>Generate / Refresh Variants</b>. To add a new color value to an existing Color option, just select Color and type the new value — it will be merged in.
            </div>

            {/* Option builder row */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
              <div>
                <div style={S.label}>Option type</div>
                <select
                  style={{ ...S.select, minWidth: 130 }}
                  value={optionTypeInput}
                  onChange={(e) => setOptionTypeInput(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select type</option>
                  {OPTION_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={S.label}>Values (comma-separated)</div>
                <input
                  style={S.input}
                  placeholder={optionTypeInput === "Color" ? "e.g. Red, Blue, Black" : optionTypeInput === "Size" ? "e.g. S, M, L, XL" : "value1, value2"}
                  value={optionValuesInput}
                  onChange={(e) => setOptionValuesInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                  disabled={saving}
                />
              </div>
              <button type="button" style={{ ...S.btnGhost, whiteSpace: "nowrap" }} onClick={addOption} disabled={saving}>
                + Add option
              </button>
            </div>

            {/* Current options pills */}
            {options.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                {options.map((opt) => (
                  <div key={opt.name} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 700 }}>
                    <span>{opt.name}: {opt.values.join(", ")}</span>
                    <button type="button" onClick={() => removeOption(opt.name)} disabled={saving} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", fontWeight: 900, fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {options.length > 0 && (
              <button type="button" style={{ ...S.btnPrimary, marginBottom: 14 }} onClick={generateVariants} disabled={saving}>
                ⚡ Generate / Refresh Variants
              </button>
            )}
          </div>

          {variants.length > 0 ? (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12, color: "var(--text-secondary)" }}>
                {variants.length} variant(s)
              </div>

              {colorOption ? (
                <div style={S.colorCardsGrid}>
                  {colorGroups.map((group) => {
                    const colorKey = `${normLower(colorOption.name)}:${normLower(group.color)}`;
                    return (
                      <div key={group.color} style={S.colorCard}>
                        <div style={S.colorCardHeader}>
                          <div style={{ fontWeight: 900 }}>{group.color}</div>
                          <div style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 700 }}>
                            {group.rows.length} size(s)
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                          <label style={{ ...S.btnGhost, padding: "6px 10px", fontSize: 12 }}>
                            {uploadingColorKey === colorKey ? "Uploading..." : `Upload images for ${group.color} (max 6)`}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadForColor(group.color, e)}
                              style={{ display: "none" }}
                              disabled={saving || uploadingColorKey === colorKey}
                            />
                          </label>

                          <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 700 }}>
                            {group.images.length} image(s)
                          </span>

                          <input
                            value={colorStockDraft[normLower(group.color)] ?? ""}
                            onChange={(e) =>
                              setColorStockDraft((prev) => ({ ...prev, [normLower(group.color)]: e.target.value }))
                            }
                            style={{ ...S.input, maxWidth: 120, padding: "8px 10px" }}
                            placeholder="Stock"
                            disabled={saving}
                          />
                          <button
                            type="button"
                            style={{ ...S.btnGhost, padding: "6px 10px", fontSize: 12 }}
                            onClick={() => {
                              const stock = Number(colorStockDraft[normLower(group.color)]);
                              if (!Number.isFinite(stock) || stock < 0) {
                                setErr("Box stock must be a number >= 0.");
                                return;
                              }
                              setVariants((prev) =>
                                prev.map((v) => {
                                  const c = normLower(optionValueOf(v, colorOption.name));
                                  if (c !== normLower(group.color)) return v;
                                  return { ...v, stock: Math.max(0, Math.floor(stock)) };
                                })
                              );
                            }}
                            disabled={saving}
                          >
                            Apply stock to this box
                          </button>
                        </div>

                        {group.images.length > 0 ? (
                          <div style={S.variantImageGrid}>
                            {group.images.map((u) => (
                              <div key={`${group.color}-${u}`} style={S.variantImageCard}>
                                <img src={toAbsUrl(u)} alt={group.color} style={S.variantImageThumb} />
                                <button
                                  type="button"
                                  style={S.variantImageRemove}
                                  onClick={() => removeColorImage(group.color, u)}
                                  disabled={saving}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        <div style={S.sizeRowsWrap}>
                          <div style={S.sizeRowHeader}>
                            <span>Size</span>
                            <span>Stock *</span>
                            <span>SKU</span>
                            <span>Price override</span>
                          </div>

                          {group.rows.map(({ size, variant }) => (
                            <div key={`${group.color}-${size || "default"}`} style={S.sizeRow}>
                              <span style={S.sizeCellLabel}>{size || "-"}</span>
                              <input
                                value={variant?.stock ?? 0}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  updateVariantByColorAndSize(group.color, size, {
                                    stock: Number.isFinite(val) ? Math.max(0, Math.floor(val)) : 0,
                                  });
                                }}
                                style={S.variantInput}
                                disabled={saving || !variant}
                              />
                              <input
                                value={variant?.sku || ""}
                                onChange={(e) => updateVariantByColorAndSize(group.color, size, { sku: e.target.value })}
                                style={S.variantInput}
                                disabled={saving || !variant}
                              />
                              <input
                                value={variant?.priceOverride ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value === "" ? "" : Number(e.target.value);
                                  updateVariantByColorAndSize(group.color, size, {
                                    priceOverride: val === "" || Number.isFinite(val) ? val : "",
                                  });
                                }}
                                style={S.variantInput}
                                placeholder={`default ${price || ""}`}
                                disabled={saving || !variant}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div style={{ color: "var(--text-secondary)", fontWeight: 700, marginBottom: 8 }}>
                    No groupable option found (e.g. color, style). Edit stock/SKU per variant below:
                  </div>
                  <div style={S.sizeRowsWrap}>
                    <div style={S.sizeRowHeader}>
                      <span>Variant</span>
                      <span>Stock *</span>
                      <span>SKU</span>
                      <span>Price override</span>
                    </div>
                    {variants.map((v, idx) => {
                      const label = (v.optionValues || []).map((ov) => `${ov.name}: ${ov.value}`).join(", ") || v.sku || `Variant ${idx + 1}`;
                      return (
                        <div key={idx} style={S.sizeRow}>
                          <span style={S.sizeCellLabel}>{label}</span>
                          <input
                            value={v.stock ?? 0}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setVariants((prev) => prev.map((x, i) => i === idx ? { ...x, stock: Number.isFinite(val) ? Math.max(0, Math.floor(val)) : 0 } : x));
                            }}
                            style={S.variantInput}
                            disabled={saving}
                          />
                          <input
                            value={v.sku || ""}
                            onChange={(e) => setVariants((prev) => prev.map((x, i) => i === idx ? { ...x, sku: e.target.value } : x))}
                            style={S.variantInput}
                            disabled={saving}
                          />
                          <input
                            value={v.priceOverride ?? ""}
                            onChange={(e) => {
                              const val = e.target.value === "" ? "" : Number(e.target.value);
                              setVariants((prev) => prev.map((x, i) => i === idx ? { ...x, priceOverride: val === "" || Number.isFinite(val) ? val : "" } : x));
                            }}
                            style={S.variantInput}
                            placeholder={`default ${price || ""}`}
                            disabled={saving}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* AI Targeting Section */}
          <div style={{ gridColumn: "1 / -1", marginTop: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 12 }}>
              🎯 AI Mall Targeting
            </div>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 12 }}>
              Help AI understand who this product is for and improve mall placement
            </div>
          </div>

          <div>
            <div style={S.label}>Genders</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["male", "female", "unisex"].map((g) => (
                <label key={g} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={targetGenders.includes(g)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTargetGenders([...targetGenders, g]);
                      } else {
                        setTargetGenders(targetGenders.filter(x => x !== g));
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ textTransform: "capitalize", fontSize: 14, fontWeight: 600 }}>{g}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }} />

          <div>
            <div style={S.label}>Min Age</div>
            <input
              style={S.input}
              type="number"
              placeholder="e.g., 18"
              value={targetMinAge}
              onChange={(e) => setTargetMinAge(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>Max Age</div>
            <input
              style={S.input}
              type="number"
              placeholder="e.g., 65"
              value={targetMaxAge}
              onChange={(e) => setTargetMaxAge(e.target.value)}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <div style={S.label}>Interests (comma-separated)</div>
            <input
              style={S.input}
              placeholder="e.g., Fashion, Tech, Travel"
              value={targetInterests.join(", ")}
              onChange={(e) => setTargetInterests(
                e.target.value.split(",").map(s => s.trim()).filter(Boolean)
              )}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <div style={S.label}>Sports (comma-separated)</div>
            <input
              style={S.input}
              placeholder="e.g., Football, Basketball, Running"
              value={targetSports.join(", ")}
              onChange={(e) => setTargetSports(
                e.target.value.split(",").map(s => s.trim()).filter(Boolean)
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Styles
   ========================= */
const S = {
  page: { 
    color: "var(--text-primary)", 
    fontFamily: "Arial", 
    padding: 14,
    background: "var(--bg-primary)",
    minHeight: "100vh"
  },
  top: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center" 
  },

  card: {
    marginTop: 12,
    background: "var(--card-bg)",
    border: "1px solid var(--border-color)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },

  h1: { 
    fontSize: 20, 
    fontWeight: 900,
    color: "var(--text-primary)"
  },
  sub: { 
    color: "var(--text-secondary)", 
    marginTop: 4, 
    fontWeight: 600,
    fontSize: 13
  },

  okBox: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(34, 197, 94, 0.4)",
    background: "rgba(34, 197, 94, 0.12)",
    fontWeight: 900,
    color: "var(--success)"
  },
  errBox: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(248, 113, 113, 0.4)",
    background: "rgba(248, 113, 113, 0.12)",
    fontWeight: 900,
    color: "var(--danger)"
  },

  grid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  label: { 
    fontWeight: 700, 
    marginBottom: 6,
    color: "var(--text-primary)",
    fontSize: 13
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
  },

  textarea: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "Arial",
    resize: "vertical"
  },

  select: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    fontSize: 14,
    outline: "none",
    cursor: "pointer"
  },

  btnPrimary: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--btn-outline-border)",
    background: "var(--btn-primary-bg)",
    color: "var(--btn-primary-text)",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 14,
    transition: "all 0.2s",
  },
  btnGhost: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--btn-outline-border)",
    background: "var(--btn-secondary-bg)",
    color: "var(--btn-secondary-text)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    transition: "all 0.2s",
  },

  colorCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 12,
  },
  colorCard: {
    border: "1px solid var(--border-color)",
    borderRadius: 14,
    background: "var(--bg-secondary)",
    padding: 12,
  },
  colorCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  variantImageGrid: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  variantImageCard: {
    position: "relative",
    width: 64,
    height: 64,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid var(--border-color)",
  },
  variantImageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  variantImageRemove: {
    position: "absolute",
    top: 2,
    right: 2,
    border: "none",
    borderRadius: 999,
    width: 18,
    height: 18,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    lineHeight: "18px",
    padding: 0,
  },
  sizeRowsWrap: {
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    overflow: "hidden",
  },
  sizeRowHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: 8,
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 800,
    color: "var(--text-secondary)",
    borderBottom: "1px solid var(--border-color)",
  },
  sizeRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: 8,
    padding: "8px 10px",
    alignItems: "center",
    borderBottom: "1px solid var(--border-color)",
  },
  sizeCellLabel: {
    fontWeight: 700,
    color: "var(--text-primary)",
    fontSize: 13,
  },
  photoUploadBox: {
    width: 80,
    height: 80,
    borderRadius: 10,
    border: "2px dashed var(--border-color)",
    background: "var(--bg-secondary)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--text-secondary)",
    fontWeight: 900,
    fontSize: 20,
    transition: "border-color 0.2s",
  },
  variantInput: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--border-color)",
    background: "var(--bg-primary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    fontSize: 13,
    outline: "none",
  },
};
